import { FC } from "react";
import styles from "./SavedCard.module.scss";
import { capitalize } from "utils/capitalize";
import Button from "components/atoms/Button";
import { trpc } from "utils/trpc";
import { raiseException } from "../../../utils/raiseException";

interface SavedCardProps {
  id: string;
  last4: string;
  brand: string;
  isDefault: boolean;
  isSelected?: boolean;
  isSelectable?: boolean;
  hideDelete?: boolean;
  hideSetDefault?: boolean;
  showDefaultLabel?: boolean;
  onChange: () => void;
  onClick?: () => void;
}

export const SavedCard: FC<SavedCardProps> = ({
  id,
  last4,
  brand,
  isDefault,
  isSelected,
  isSelectable,
  hideDelete,
  hideSetDefault,
  showDefaultLabel,
  onChange,
  onClick,
}) => {
  const removePaymentMethod =
    trpc.mainService.payments.stripe.removePaymentMethod.useMutation();
  const setDefaultPaymentMethod =
    trpc.mainService.payments.stripe.setDefaultPaymentMethod.useMutation();

  return (
    <div
      className={`${styles.savedCard} ${
        isSelected ? styles.selectedSavedCard : ""
      } ${isSelectable ? styles.selectableSavedCard : ""}`}
      onClick={onClick}
    >
      <div className={styles.cardInfoContainer}>
        <svg
          width="44"
          height="44"
          viewBox="0 0 44 44"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="44" height="44" rx="22" fill="#DFEAFF" />
          <rect
            x="10.207"
            y="13.9219"
            width="23.586"
            height="16.1574"
            rx="1.44905"
            fill="white"
            stroke="#F3F3F3"
            strokeWidth="0.414014"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18.7938 26.5112V27.123V27.7349H18.5259V27.5863C18.441 27.6965 18.3121 27.7656 18.1369 27.7656C17.7914 27.7656 17.5206 27.4969 17.5206 27.123C17.5206 26.7494 17.7914 26.4804 18.1369 26.4804C18.3121 26.4804 18.441 26.5496 18.5259 26.6597V26.5112H18.7938ZM18.1701 26.7314C17.9383 26.7314 17.7964 26.9081 17.7964 27.1232C17.7964 27.3383 17.9383 27.515 18.1701 27.515C18.3916 27.515 18.5412 27.3459 18.5412 27.1232C18.5412 26.9005 18.3916 26.7314 18.1701 26.7314ZM27.8451 27.1232C27.8451 26.9081 27.987 26.7314 28.2189 26.7314C28.4407 26.7314 28.5899 26.9005 28.5899 27.1232C28.5899 27.3459 28.4407 27.515 28.2189 27.515C27.987 27.515 27.8451 27.3383 27.8451 27.1232ZM28.8428 26.0198V27.123V27.7349H28.5747V27.5863C28.4897 27.6965 28.3608 27.7656 28.1856 27.7656C27.8402 27.7656 27.5694 27.4969 27.5694 27.123C27.5694 26.7494 27.8402 26.4804 28.1856 26.4804C28.3608 26.4804 28.4897 26.5496 28.5747 26.6597V26.0198H28.8428ZM22.1184 26.7184C22.291 26.7184 22.4019 26.8259 22.4302 27.0152H21.791C21.8196 26.8385 21.9275 26.7184 22.1184 26.7184ZM21.5104 27.123C21.5104 26.7415 21.7629 26.4804 22.1239 26.4804C22.4691 26.4804 22.7062 26.7415 22.7089 27.123C22.7089 27.1588 22.7062 27.1922 22.7036 27.2253H21.7888C21.8275 27.4456 21.9847 27.525 22.1573 27.525C22.2809 27.525 22.4124 27.4787 22.5157 27.397L22.6469 27.5942C22.4974 27.7196 22.3275 27.7656 22.1419 27.7656C21.7732 27.7656 21.5104 27.5122 21.5104 27.123ZM25.3578 27.1232C25.3578 26.9081 25.4997 26.7314 25.7315 26.7314C25.9531 26.7314 26.1026 26.9005 26.1026 27.1232C26.1026 27.3459 25.9531 27.515 25.7315 27.515C25.4997 27.515 25.3578 27.3383 25.3578 27.1232ZM26.3552 26.5112V27.123V27.7349H26.0874V27.5863C26.0021 27.6965 25.8735 27.7656 25.6983 27.7656C25.3528 27.7656 25.0821 27.4969 25.0821 27.123C25.0821 26.7494 25.3528 26.4804 25.6983 26.4804C25.8735 26.4804 26.0021 26.5496 26.0874 26.6597V26.5112H26.3552ZM23.8457 27.123C23.8457 27.4943 24.1058 27.7656 24.5029 27.7656C24.6884 27.7656 24.812 27.7246 24.946 27.6197L24.8173 27.4046C24.7168 27.4764 24.6111 27.5148 24.4949 27.5148C24.2811 27.5122 24.1238 27.3586 24.1238 27.123C24.1238 26.8874 24.2811 26.7339 24.4949 26.7312C24.6111 26.7312 24.7168 26.7696 24.8173 26.8414L24.946 26.6263C24.812 26.5214 24.6884 26.4804 24.5029 26.4804C24.1058 26.4804 23.8457 26.7517 23.8457 27.123ZM26.9737 26.6598C27.0433 26.5522 27.1439 26.4804 27.2984 26.4804C27.3527 26.4804 27.43 26.4907 27.4893 26.5138L27.4067 26.7647C27.3501 26.7415 27.2934 26.7339 27.2392 26.7339C27.0639 26.7339 26.9763 26.8464 26.9763 27.0489V27.7349H26.7082V26.5112H26.9737V26.6598ZM20.1189 26.6085C19.99 26.5241 19.8124 26.4804 19.6165 26.4804C19.3044 26.4804 19.1036 26.629 19.1036 26.8722C19.1036 27.0717 19.2531 27.1948 19.5286 27.2332L19.6552 27.2511C19.8021 27.2716 19.8714 27.31 19.8714 27.3791C19.8714 27.4738 19.7735 27.5277 19.5906 27.5277C19.405 27.5277 19.2711 27.4688 19.1808 27.3996L19.0546 27.6068C19.2015 27.7143 19.387 27.7656 19.5879 27.7656C19.9437 27.7656 20.1499 27.5992 20.1499 27.3662C20.1499 27.1511 19.9876 27.0386 19.7195 27.0002L19.5932 26.9821C19.4773 26.9668 19.3844 26.944 19.3844 26.8619C19.3844 26.7723 19.472 26.7186 19.6189 26.7186C19.7761 26.7186 19.9283 26.7775 20.003 26.8235L20.1189 26.6085ZM23.25 26.6598C23.3193 26.5522 23.4199 26.4804 23.5745 26.4804C23.6287 26.4804 23.706 26.4907 23.7653 26.5138L23.6827 26.7647C23.6261 26.7415 23.5694 26.7339 23.5152 26.7339C23.3399 26.7339 23.2523 26.8464 23.2523 27.0489V27.7349H22.9845V26.5112H23.25V26.6598ZM21.2885 26.5112H20.8504V26.1399H20.5796V26.5112H20.3298V26.7544H20.5796V27.3126C20.5796 27.5965 20.6905 27.7656 21.0074 27.7656C21.1236 27.7656 21.2575 27.7299 21.3425 27.671L21.2652 27.443C21.1852 27.489 21.0976 27.5122 21.028 27.5122C20.8941 27.5122 20.8504 27.4301 20.8504 27.3073V26.7544H21.2885V26.5112ZM17.2837 26.9669V27.7349H17.013V27.0539C17.013 26.8464 16.9253 26.7313 16.7425 26.7313C16.5646 26.7313 16.441 26.8441 16.441 27.0565V27.7349H16.1702V27.0539C16.1702 26.8464 16.0802 26.7313 15.9023 26.7313C15.7192 26.7313 15.6006 26.8441 15.6006 27.0565V27.7349H15.3301V26.5112H15.5982V26.6621C15.6988 26.5188 15.8274 26.4804 15.959 26.4804C16.1472 26.4804 16.2811 26.5625 16.3661 26.6982C16.4796 26.5264 16.6419 26.4778 16.7991 26.4804C17.0982 26.4831 17.2837 26.6776 17.2837 26.9669Z"
            fill="#231F20"
          />
          <path
            d="M24.0889 24.4327H20.0293V17.1855H24.0889V24.4327Z"
            fill="#FF5F00"
          />
          <path
            d="M20.2872 20.8089C20.2872 19.3388 20.9801 18.0292 22.0592 17.1853C21.2701 16.5682 20.2742 16.1999 19.1919 16.1999C16.6297 16.1999 14.5527 18.2634 14.5527 20.8089C14.5527 23.3545 16.6297 25.418 19.1919 25.418C20.2742 25.418 21.2701 25.0496 22.0592 24.4325C20.9801 23.5886 20.2872 22.279 20.2872 20.8089Z"
            fill="#EB001B"
          />
          <path
            d="M29.5653 20.8089C29.5653 23.3545 27.4883 25.418 24.9261 25.418C23.8438 25.418 22.848 25.0496 22.0586 24.4325C23.1379 23.5886 23.8309 22.279 23.8309 20.8089C23.8309 19.3388 23.1379 18.0292 22.0586 17.1853C22.848 16.5682 23.8438 16.1999 24.9261 16.1999C27.4883 16.1999 29.5653 18.2634 29.5653 20.8089Z"
            fill="#F79E1B"
          />
        </svg>

        <div className={styles.cardInfo}>
          <span>{brand && capitalize(brand)}</span>
          <span>****{last4}</span>
        </div>
      </div>
      <div className={styles.actionButtonContainer}>
        {isDefault && showDefaultLabel && (
          <span className={styles.defaultLabel}>Predefinita</span>
        )}
        {!hideSetDefault && !isDefault && (
          <Button
            className={styles.actionButton}
            variant="tertiary"
            disabled={
              removePaymentMethod.isLoading || setDefaultPaymentMethod.isLoading
            }
            onClick={async (event) => {
              try {
                event.preventDefault();
                event.stopPropagation();
                await setDefaultPaymentMethod.mutateAsync({
                  paymentMethodId: id,
                });
                await onChange();
              } catch (e: any) {
                raiseException(e);
                console.error(e);
              }
            }}
          >
            Imposta come predefinita
          </Button>
        )}
        {!hideDelete && (
          <Button
            className={styles.actionButton}
            variant="tertiary"
            disabled={
              removePaymentMethod.isLoading || setDefaultPaymentMethod.isLoading
            }
            onClick={async (event) => {
              try {
                event.preventDefault();
                event.stopPropagation();
                await removePaymentMethod.mutateAsync({
                  paymentMethodId: id,
                });
                await onChange();
              } catch (e: any) {
                raiseException(e);
                console.error(e);
              }
            }}
          >
            Elimina
          </Button>
        )}
      </div>
    </div>
  );
};
